/** @fileoverview Bulk-assign an energy source to the selected search results. */
import { useState } from "react";

/**
 * Assigns one energy source to all currently-selected, visible search results.
 * @param {object} props Component props.
 * @param {Array<{id:number,name:string}>} props.sources Available energy sources.
 * @param {object[]} props.visibleQuestions Questions currently visible (search results).
 * @param {Set<number>} props.pendingIds Currently selected KPI ids.
 * @param {Record<number, number>} props.kpiSourceMap Per-KPI source assignments.
 * @param {(kpiId:number, sourceId:number)=>void} props.onKpiSourceChange Assignment handler.
 * @param {boolean} props.disabled Disable interaction.
 * @returns {JSX.Element|null} The bulk bar, or null when not applicable.
 */
export default function BulkSourceAssignBar({ sources, visibleQuestions, pendingIds, kpiSourceMap, onKpiSourceChange, disabled }) {
  const [bulkSource, setBulkSource] = useState("");
  if (disabled || !sources.length || !visibleQuestions.length) return null;

  const selectedVisible = visibleQuestions.filter(q => pendingIds.has(q.questionId));
  const assignedCount = selectedVisible.filter(q => kpiSourceMap[q.questionId]).length;

  const applyBulk = (sourceId) => {
    setBulkSource(sourceId);
    if (!sourceId) return;
    selectedVisible.forEach(q => onKpiSourceChange(q.questionId, Number(sourceId)));
  };

  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 10, padding: "10px 16px",
      background: "var(--es-sky-50)", border: "1px solid var(--es-sky-200)", borderRadius: 10, marginBottom: 10
    }}>
      <span style={{ fontSize: 11, fontWeight: 700, color: "var(--es-cyan-700)", whiteSpace: "nowrap" }}>
        ⚡ Bulk assign to {selectedVisible.length} selected search results:
      </span>
      <select
        className="es-select"
        value={bulkSource}
        onChange={e => applyBulk(e.target.value)}
        style={{
          padding: "5px 24px 5px 10px", borderRadius: 7, border: "1.5px solid var(--es-blue-300)",
          fontSize: 11, fontWeight: 600, color: "var(--es-cyan-700)", background: "var(--es-white)",
          cursor: "pointer",
        }}
      >
        <option value="">Choose a source…</option>
        {sources.map(src => <option key={src.id} value={src.id}>{src.name}</option>)}
      </select>
      {assignedCount > 0 && (
        <span style={{ fontSize: 11, color: "var(--es-source)", fontWeight: 600 }}>
          {assignedCount}/{selectedVisible.length} already assigned
        </span>
      )}
    </div>
  );
}
